import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons";

import { WorkbookVariantType } from "../Workbook";
import ExplainFingerprint from "features/Explain/ExplainFingerprint";
import { Link, useParams } from "react-router-dom";
import { useRoutes } from "utils/routes";
import { SpinnerIcon } from "ui/Icons";
import classNames from "classnames";
import {
  QueryTuningInsight,
  getAnnotationTitle,
  getInsightDetails,
  insightTypeToAnnotationType,
} from "utils/explain";

type InsightType = "NestedLoop" | "OrderByLimit" | "OrToUnion";

export type NodeInsightType = {
  type: InsightType;
  insight: QueryTuningInsight;
  variantId: string;
  resultId: string;
  resultFingerprint: string;
  applied: boolean;
  inner_table_id?: number;
  index_used?: string;
  join_filter?: string;
};

const InsightTile = ({
  workbookId,
  insight,
  onApply,
  linkTo,
  linkText,
  error,
  loading,
  hideDescription,
}: {
  workbookId: string;
  insight: NodeInsightType;
  onApply?: () => void;
  linkTo?: string;
  linkText?: string;
  error?: string;
  loading?: boolean;
  hideDescription?: boolean;
}) => {
  const { databaseId } = useParams();
  const { databaseWorkbookVariantResult } = useRoutes();
  const title = getAnnotationTitle(insightTypeToAnnotationType(insight.type));
  const details = getInsightDetails(insight.type);

  return (
    <div className="border rounded bg-slate-50 px-4 py-3 mb-2">
      <div className="font-medium mb-1">{title}</div>
      {!hideDescription && (
        <div className="mb-2">
          <ul className="!ml-[16px]">
            {details.map((detail, i) => {
              return <li key={i}>{detail}</li>;
            })}
          </ul>
        </div>
      )}
      {error && onApply && (
        <div className="text-[#C22426] mb-2">
          <div>
            <FontAwesomeIcon icon={faExclamationCircle} /> Applying changes to
            this query is not supported.
          </div>
          <div>
            <small>{error}</small>
          </div>
        </div>
      )}
      {(insight.applied || (!error && onApply) || linkTo) && (
        <div className="flex gap-2 items-center justify-between">
          {insight.applied ? (
            <div>
              <FontAwesomeIcon icon={faCheck} /> Applied
            </div>
          ) : onApply ? (
            <button
              disabled={loading}
              className="btn btn-primary relative"
              onClick={onApply}
            >
              <div
                className={classNames(
                  "absolute grid place-items-center left-0 right-0 top-0 bottom-0",
                  !loading && "invisible",
                )}
              >
                <SpinnerIcon className="text-white text-lg" />
              </div>
              <span className={loading ? "invisible" : ""}>Apply</span>
            </button>
          ) : linkTo ? (
            <Link to={linkTo} className="btn btn-success">
              {linkText}
            </Link>
          ) : null}
          <div className="inline-flex gap-1">
            Found in
            <Link
              to={databaseWorkbookVariantResult(
                databaseId,
                workbookId,
                insight.variantId,
                insight.resultId,
              )}
            >
              <ExplainFingerprint fingerprint={insight.resultFingerprint} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export const variantInsights = (variant: WorkbookVariantType) => {
  const insights: NodeInsightType[] = [];
  variant.workbookResults.forEach((result) => {
    const nodeInsights = result.nodeInsights ?? [];
    nodeInsights.forEach((parsedInsight: QueryTuningInsight) => {
      if (
        parsedInsight.type !== "NestedLoop" &&
        parsedInsight.type !== "OrderByLimit" &&
        parsedInsight.type !== "OrToUnion"
      ) {
        return;
      }
      // To avoid listing up duplicated insights, unique by the key param
      if (
        insights.every((ins) => {
          if (parsedInsight.type === "NestedLoop") {
            return ins["inner_table_id"] !== parsedInsight["inner_table_id"];
          } else if (parsedInsight.type === "OrderByLimit") {
            return ins["index_used"] !== parsedInsight["index_used"];
          } else if (parsedInsight.type === "OrToUnion") {
            return ins["join_filter"] !== parsedInsight["join_filter"];
          } else {
            // For other type of insights, do not show in the list for now
            return false;
          }
        })
      ) {
        insights.push({
          type: parsedInsight.type,
          insight: parsedInsight,
          applied: false,
          variantId: variant.id,
          resultId: result.id,
          resultFingerprint: result.planFingerprint,
        });
      }
    });
  });
  return insights;
};

export default InsightTile;
